import type { Lang } from '../core/language.service';
import {
  coerceTemplate,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_TEMPLATE,
  DEFAULT_TEXT_SIZE,
  TEXT_SIZE_RANGE,
} from '../core/constants';
import {
  Banner,
  MenuCategory,
  MenuItem,
  MenuVariant,
  ModificationGroup,
  Promos,
  Restaurant,
  SocialKind,
  SocialLink,
} from './models';
import {
  QrBanner,
  QrMenuResponse,
  QrOrgResponse,
  QrPosition,
  QrPromo,
  QrPromosResponse,
  QrSetting,
} from './qr.types';

const SOCIAL_SETTING_KEYS: ReadonlyArray<readonly [SocialKind, string]> = [
  ['instagram', 'qrInstagram'],
  ['telegram', 'qrTelegram'],
  ['youtube', 'qrYoutube'],
  ['meta', 'qrMeta'],
];

/**
 * Localized field of a bilingual backend record: `base` is the Uzbek value (always sent), `ru`
 * the Russian one. A missing or blank Ru value falls back to the base — an empty translation
 * must never render as an empty name.
 */
export function pick(lang: Lang, base: string | null | undefined, ru?: string | null): string {
  if (lang === 'ru' && ru?.trim()) return ru;
  return base ?? '';
}

function settingsToMap(settings: QrSetting[] | undefined): Record<string, string> {
  return Object.fromEntries((settings ?? []).map((s) => [s.key, s.value]));
}
export function buildLogoSrc(logo: string | null | undefined): string | undefined {
  if (!logo) return undefined;
  if (logo.startsWith('data:') || logo.startsWith('http')) return logo;
  // Чистый base64 без префикса трактуем как PNG: бэкенд не передаёт MIME отдельно.
  return `data:image/png;base64,${logo}`;
}

/** Admins paste schemeless links; a bare "instagram.com/x" would resolve as a relative path. */
function toExternalUrl(value: string | undefined): string | undefined {
  const v = value?.trim();
  if (!v) return undefined;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

function mapSocials(settings: Record<string, string>): SocialLink[] {
  return SOCIAL_SETTING_KEYS.flatMap(([kind, key]) => {
    const url = toExternalUrl(settings[key]);
    return url ? [{ kind, url }] : [];
  });
}

/** Reads a boolean-ish setting (1/true/on/yes vs 0/false/off/no); absent/blank => `fallback`. */
function readFlag(settings: Record<string, string>, key: string, fallback = false): boolean {
  const v = settings[key]?.trim();
  if (!v) return fallback;
  if (/^(1|true|on|yes)$/i.test(v)) return true;
  if (/^(0|false|off|no)$/i.test(v)) return false;
  return fallback;
}

/** Reads an integer setting rounded and clamped to [min, max]; absent/blank/non-numeric => `fallback`. */
function readClampedInt(
  settings: Record<string, string>,
  key: string,
  min: number,
  max: number,
  fallback: number,
): number {
  const raw = settings[key]?.trim();
  if (!raw) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function mapOrgToRestaurant(org: QrOrgResponse): Restaurant {
  const settings = settingsToMap(org.settings);
  return {
    id: String(org.orgId),
    name: org.name,
    tagline: settings['tagLine'] || undefined,
    primaryColor: settings['primaryColor'] || DEFAULT_PRIMARY_COLOR,
    logoUrl: buildLogoSrc(org.logo),
    wifiName: settings['wifiName'] || undefined,
    wifiPassword: settings['wifiPassword'] || undefined,
    phone: settings['qrPhoneNumber'] || undefined,
    // 'qrAddres' — известная опечатка ключа на бэкенде; намеренный fallback, не удалять.
    address: settings['qrAddress'] || settings['qrAddres'] || undefined,
    socials: mapSocials(settings),
    actionBar: {
      callWaiter: readFlag(settings, 'qrCallWaiter'),
      bill: readFlag(settings, 'qrBill'),
    },
    ordering: readFlag(settings, 'qrOrdering'),
    orderCreate: readFlag(settings, 'qrOrderCreate'),
    textSize: readClampedInt(
      settings,
      'qrTextSize',
      TEXT_SIZE_RANGE.min,
      TEXT_SIZE_RANGE.max,
      DEFAULT_TEXT_SIZE,
    ),
    // Whitelist on the front regardless of backend validation: absent/blank/unknown/'retro' => default.
    designTemplate: coerceTemplate(settings['qrDesignTemplate']) ?? DEFAULT_TEMPLATE,
  };
}
export function mapMenuToCategories(menu: QrMenuResponse, lang: Lang): MenuCategory[] {
  return (menu.categories ?? []).flatMap((c) => {
    const id = Number(c.id);
    // Нечисловые id отбрасываем: NaN не совпал бы с position.categoryId — вечно пустая категория.
    return Number.isFinite(id) ? [{ id, name: pick(lang, c.name, c.nameRu) }] : [];
  });
}
function mapVariants(p: QrPosition, lang: Lang): MenuVariant[] {
  return (p.variants ?? []).map((v) => ({
    id: v.id,
    name: pick(lang, v.name, v.nameRu),
    price: v.price,
    properties: (v.properties ?? []).map((prop) => ({
      name: pick(lang, prop.name, prop.nameRu),
      value: pick(lang, prop.value, prop.valueRu),
    })),
  }));
}

/** Группы без items отбрасываем: гостю в них нечего показывать. */
function buildModificationGroupIndex(
  menu: QrMenuResponse,
  lang: Lang,
): Map<number, ModificationGroup> {
  const index = new Map<number, ModificationGroup>();
  for (const g of menu.modificationGroups ?? []) {
    const items = (g.items ?? []).map((i) => ({
      id: i.id,
      name: pick(lang, i.name, i.nameRu),
      price: i.price,
      maxOrderSelect: Math.max(1, i.maxOrderSelect || 1),
    }));
    if (items.length === 0) continue;
    index.set(g.id, {
      id: g.id,
      name: pick(lang, g.name, g.nameRu),
      required: g.required,
      maxSelect: g.maxSelect,
      items,
    });
  }
  return index;
}

export function mapMenuToItems(menu: QrMenuResponse, lang: Lang): MenuItem[] {
  const groups = buildModificationGroupIndex(menu, lang);
  return (menu.positions ?? []).map((p) => ({
    id: p.id,
    // pick() also coerces a null name/description (typed string, still nullable on the wire).
    name: pick(lang, p.name, p.nameRu),
    description: pick(lang, p.description, p.descriptionRu),
    price: p.price,
    imageUrl: p.imageUrl ?? '',
    categoryId: p.categoryId,
    variants: mapVariants(p, lang),
    // Битые ссылки (id без группы или группа без items) молча пропускаем.
    modificationGroups: (p.modificationGroupIds ?? []).flatMap((id) => {
      const g = groups.get(id);
      return g ? [g] : [];
    }),
  }));
}

export const NO_PROMOS: Promos = { defaultPercent: null, byId: new Map() };

function readPercent(promo: QrPromo | null | undefined): number | null {
  const amount = promo?.amount;
  return typeof amount === 'number' && amount > 0 && amount <= 100 ? amount : null;
}

/** Граница доверия: битая запись отбрасывается по одной, ответ целиком не ломается. */
export function mapPromos(res: QrPromosResponse | null): Promos {
  const byId = new Map<string, number>();
  for (const [id, promo] of Object.entries(res?.positions ?? {})) {
    const percent = readPercent(promo);
    if (percent !== null) byId.set(id, percent);
  }
  return { defaultPercent: readPercent(res?.defaultPromo), byId };
}

/** Строго по языку, без pick(): на картинке баннера текст на своём языке. */
export function mapBanners(res: (QrBanner | null)[] | null, lang: Lang): Banner[] {
  if (!Array.isArray(res)) return [];
  return res
    .flatMap((b) => {
      const imageUrl = lang === 'ru' ? b?.imageUrlRu : b?.imageUrlUz;
      if (typeof b?.id !== 'number' || typeof imageUrl !== 'string' || !imageUrl.trim()) return [];
      const order =
        typeof b.sortOrder === 'number' && Number.isFinite(b.sortOrder)
          ? b.sortOrder
          : Number.MAX_SAFE_INTEGER;
      return [{ id: b.id, imageUrl, order }];
    })
    .sort((x, y) => x.order - y.order)
    .map(({ id, imageUrl }) => ({ id, imageUrl }));
}
